import { Container } from "@/components/ui/container";
import { Link } from "react-router-dom";
import {
    Bot,
    PhoneCall,
    PhoneMissed,
    UserCheck,
    CalendarCheck,
    Workflow,
    Star,
    Send,
    FileSignature,
    ArrowUpRight,
    type LucideIcon,
} from "lucide-react";

// `href` is set only where a capability has a dedicated /platform/* spoke page
// with a true 1:1 topic match: those cards become internal links; the rest
// stay static. Don't link a card to a spoke that isn't the same intent.
type Capability = { icon: LucideIcon; title: string; desc: string; href?: string };

const capabilities: Capability[] = [
    {
        icon: Bot,
        title: "AI Lead Response",
        desc: "Every inquiry gets answered in seconds, day or night.",
        href: "/platform/speed-to-lead/",
    },
    {
        icon: PhoneCall,
        title: "AI Receptionist",
        desc: "A 24/7 voice assistant that answers, qualifies, and books for you.",
        href: "/platform/ai-receptionist/",
    },
    {
        icon: PhoneMissed,
        title: "Missed-Call Follow-Up",
        desc: "Every missed call gets an instant text back before they call the next guy.",
        href: "/platform/missed-call-text-back/",
    },
    {
        icon: UserCheck,
        title: "Lead Qualification",
        desc: "Filter out tire-kickers so your team only talks to ready-to-buy customers.",
    },
    {
        icon: CalendarCheck,
        title: "Appointment Booking",
        desc: "Leads book themselves straight into your calendar. No phone tag.",
    },
    {
        icon: Workflow,
        title: "Pipeline Automation",
        desc: "Every deal moves forward on its own, so nothing slips through the cracks.",
    },
    {
        icon: Star,
        title: "Reviews & Reputation",
        desc: "Automatically ask for reviews at the right moment and turn 5-star feedback into new customers.",
        href: "/platform/reputation-management/",
    },
    {
        icon: Send,
        title: "Follow-Up Sequences",
        desc: "Automated email and SMS that nurture every lead until they're ready to buy.",
    },
    {
        icon: FileSignature,
        title: "Estimates, Invoicing & Document Signing",
        desc: "Send quotes, collect payment, and get contracts signed, all in one place.",
    },
];

type CapabilitiesSectionProps = {
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    className?: string;
};

export function CapabilitiesSection({
    eyebrow = "WHAT FRAYZE AUTOMATES",
    title = "Every step from first contact to signed deal",
    subtitle = "One connected system handles the busywork, so you capture more leads, book more jobs, and never drop the ball.",
    className = "py-24 bg-background border-t border-border/60",
}: CapabilitiesSectionProps = {}) {
    return (
        <section className={className}>
            <Container>
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="mb-5"><span className="eyebrow">{eyebrow}</span></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6">
                        {title}
                    </h2>
                    <p className="text-xl text-muted-foreground">
                        {subtitle}
                    </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {capabilities.map((c) => {
                        const inner = (
                            <>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-accent/10 ring-1 ring-primary/10 flex items-center justify-center text-primary group-hover:from-primary/25 group-hover:to-accent/15 transition-colors">
                                        <c.icon className="w-6 h-6" />
                                    </div>
                                    {c.href && (
                                        <ArrowUpRight className="w-5 h-5 text-muted-foreground/30 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" aria-hidden="true" />
                                    )}
                                </div>
                                <h3 className="text-lg font-bold mb-2">{c.title}</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                                {c.href && (
                                    <span className="mt-3 inline-block text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity">Learn more</span>
                                )}
                            </>
                        );

                        return c.href ? (
                            <Link
                                key={c.title}
                                to={c.href}
                                className="group premium-card premium-card-hover p-6 block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                            >
                                {inner}
                            </Link>
                        ) : (
                            <div
                                key={c.title}
                                className="group premium-card premium-card-hover p-6"
                            >
                                {inner}
                            </div>
                        );
                    })}
                </div>
            </Container>
        </section>
    );
}
